import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../../utils/api';
import config from '../../../config';
import deviceService from '../../../services/deviceService';
import i18n from '../../../i18n';
import {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchDevices,
  fetchDevicesSuccess,
  fetchDevicesFailure,
  fetchDeviceById,
  fetchDeviceByIdSuccess,
  fetchDeviceByIdFailure,
  addDevice,
  addDeviceSuccess,
  addDeviceFailure,
  updateDevice,
  updateDeviceSuccess,
  updateDeviceFailure,
  deleteDevice,
  deleteDeviceSuccess,
  deleteDeviceFailure,
  bulkDeleteDevices,
  bulkDeleteDevicesSuccess,
  bulkDeleteDevicesFailure,
  approveDevice,
  approveDeviceSuccess,
  approveDeviceFailure,
  rejectDevice,
  rejectDeviceSuccess,
  rejectDeviceFailure,
  revokeDevice,
  revokeDeviceSuccess,
  revokeDeviceFailure,
  toggleLGSPAccess,
  toggleLGSPAccessSuccess,
  toggleLGSPAccessFailure,
  updateConnectionStatus,
  updateConnectionStatusSuccess,
  updateConnectionStatusFailure,
  verifyCitizen,
  verifyCitizenSuccess,
  verifyCitizenFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  assignUnit,
  assignUnitSuccess,
  assignUnitFailure,
  importDevices,
  importDevicesSuccess,
  importDevicesFailure,
  exportDevices,
  exportDevicesSuccess,
  exportDevicesFailure,
  downloadTemplate,
  downloadTemplateSuccess,
  downloadTemplateFailure,
  bulkReapproveDevices,
  bulkReapproveDevicesSuccess,
  bulkReapproveDevicesFailure,
  clearError,
} from './slice';

// Error message mapping
const ERROR_MESSAGES = {
  'Device code already exists': 'devices.errors.codeExists',
  'Device not found': 'devices.errors.notFound',
  'Invalid serial number': 'devices.errors.invalidSerialNumber',
};

function* handleFetchInit() {
  try {
    const response = yield call(deviceService.getInit);
    yield put(fetchInitSuccess(response));
  } catch (error) {
    const errorMessage = error.message;
    yield put(fetchInitFailure(errorMessage));
  }
}

function* handleFetchDevices(action) {
  try {
    // Get page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, and filterUnits from action payload (if provided)
    const { page, pageSize, sortColumn, sortDirection, filterCode, filterStatuses, filterUnits } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    if (sortColumn) {
      params.sortColumn = sortColumn;
      params.sortDirection = sortDirection || 'asc';
    }
    if (filterCode) {
      params.deviceCode = filterCode;
    }

    // Build query string for repeated params (status and unitId)
    let queryParts = [];
    if (filterStatuses && filterStatuses.length > 0) {
      queryParts.push(...filterStatuses.map(status => `status=${status}`));
    }
    if (filterUnits && filterUnits.length > 0) {
      queryParts.push(...filterUnits.map(unitId => `unitId=${unitId}`));
    }

    // Combine base URL with params and query string
    let url = config.endpoints.devices.list;

    // Add regular params as query string
    const regularParams = new URLSearchParams(params).toString();
    if (regularParams) {
      url += `?${regularParams}`;
    }

    // Add repeated params
    if (queryParts.length > 0) {
      const separator = regularParams ? '&' : '?';
      url += `${separator}${queryParts.join('&')}`;
    }

    const response = yield call(api.get, url);
    // Pass the whole response (including pagination info)
    yield put(fetchDevicesSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(fetchDevicesFailure(errorMessage));
  }
}

function* handleFetchDeviceById(action) {
  try {
    const response = yield call(deviceService.getDeviceById, action.payload);
    yield put(fetchDeviceByIdSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(fetchDeviceByIdFailure(errorMessage));
  }
}

function* handleAddDevice(action) {
  try {
    const response = yield call(deviceService.createDevice, action.payload);
    yield put(addDeviceSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(addDeviceFailure(errorMessage));
  }
}

function* handleUpdateDevice(action) {
  try {
    const response = yield call(deviceService.updateDevice, action.payload);
    yield put(updateDeviceSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(updateDeviceFailure(errorMessage));
  }
}

function* handleDeleteDevice(action) {
  try {
    const response = yield call(deviceService.deleteDevice, action.payload);
    yield put(deleteDeviceSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(deleteDeviceFailure(errorMessage));
  }
}

function* handleBulkDeleteDevices(action) {
  try {
    const response = yield call(deviceService.bulkDeleteDevices, action.payload);
    yield put(bulkDeleteDevicesSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(bulkDeleteDevicesFailure(errorMessage));
  }
}

function* handleApproveDevice(action) {
  try {
    const response = yield call(deviceService.approveDevice, action.payload);
    yield put(approveDeviceSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(approveDeviceFailure(errorMessage));
  }
}

function* handleRejectDevice(action) {
  try {
    const { id, statusNote } = action.payload;
    const response = yield call(deviceService.rejectDevice, id, statusNote);
    yield put(rejectDeviceSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(rejectDeviceFailure(errorMessage));
  }
}

function* handleRevokeDevice(action) {
  try {
    const { id, statusNote } = action.payload;
    const response = yield call(deviceService.revokeDevice, id, statusNote);
    yield put(revokeDeviceSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(revokeDeviceFailure(errorMessage));
  }
}

function* handleToggleLGSPAccess(action) {
  try {
    const { id, allowLGSPAccess } = action.payload;
    const response = yield call(deviceService.toggleLGSPAccess, id, allowLGSPAccess);
    yield put(toggleLGSPAccessSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(toggleLGSPAccessFailure(errorMessage));
  }
}

function* handleUpdateConnectionStatus(action) {
  try {
    const { id, connectionStatus, reason } = action.payload;
    const response = yield call(deviceService.updateConnectionStatus, id, connectionStatus, reason);
    yield put(updateConnectionStatusSuccess(response));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(updateConnectionStatusFailure(errorMessage));
  }
}

function* handleVerifyCitizen(action) {
  try {
    const response = yield call(deviceService.verifyCitizen, action.payload);
    yield put(verifyCitizenSuccess(response));
    return response; // Return the response for use in the caller
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(verifyCitizenFailure(errorMessage));
    throw error; // Re-throw to allow caller to handle
  }
}

function* handleFetchUnits(action) {
  try {
    const { page, pageSize } = action.payload || {};
    const params = {};
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;

    const response = yield call(api.get, config.endpoints.units.list, params);
    yield put(fetchUnitsSuccess(response));
  } catch (error) {
    const errorMessage = error.message;
    yield put(fetchUnitsFailure(errorMessage));
  }
}

function* handleAssignUnit(action) {
  try {
    const response = yield call(deviceService.assignUnit, action.payload);
    yield put(assignUnitSuccess(response));
  } catch (error) {
    const errorMessage = error.message;
    yield put(assignUnitFailure(errorMessage));
  }
}

function* handleImportDevices(action) {
  try {
    const response = yield call(deviceService.importDevices, action.payload);
    console.log('Import response:', response);

    const successCount = response.successCount || 0;
    const failureCount = response.failureCount || 0;
    const totalRows = response.totalRows || 0;
    const errors = response.errors || [];

    // Check if file has no data
    if (totalRows === 0) {
      const noDataMessage = i18n.t('devices.import.noData');
      yield put(importDevicesFailure(noDataMessage));
      return;
    }

    // Build message
    let message = i18n.t('devices.import.completed', { successCount, totalRows });

    if (failureCount > 0) {
      message += `, ${failureCount} ${i18n.t('devices.import.failed')}`;
    }

    // Show errors/warnings even if all rows succeeded (e.g., date format warnings)
    if (errors.length > 0) {
      console.warn('Import warnings/errors:', errors);
      // Show first 5 errors/warnings
      const errorSummary = errors.slice(0, 5).join('\n');
      message += `\n\n${i18n.t('devices.import.errors')}\n${errorSummary}`;
      if (errors.length > 5) {
        message += `\n${i18n.t('devices.import.moreErrors', { count: errors.length - 5 })}`;
      }
    }

    // Determine if this should be success or failure notification
    if (failureCount > 0 && successCount === 0) {
      // All failed - show as error
      yield put(importDevicesFailure(message));
    } else if (errors.length > 0) {
      // Partial success or has warnings - show as warning/error with details
      // Add unique ID to force re-render notification even if same message
      const uniqueMessage = `${message}__${Date.now()}`;
      yield put(importDevicesFailure(uniqueMessage));
      // Still refresh the list since some devices were imported
      yield put(fetchDevices({
        page: 1,
        pageSize: 10,
        sortColumn: 'deviceCode',
        sortDirection: 'asc'
      }));
    }
    if (successCount > 0) {
      // All succeeded with no warnings - show as success
      yield put(importDevicesSuccess({ ...response, message }));
      // Refresh devices list
      yield put(fetchDevices({
        page: 1,
        pageSize: 10,
        sortColumn: 'deviceCode',
        sortDirection: 'asc'
      }));
    }
  } catch (error) {
    console.error('Import error:', error);
    let errorMessage;

    if (error.response && error.response.data) {
      errorMessage = error.response.data.message || error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    yield put(importDevicesFailure(errorMessage));
  }
}

function* handleExportDevices(action) {
  try {
    // action.payload contains: exportType ('excel' or 'pdf'), filters, and selectedIds
    const { exportType, filters, selectedIds } = action.payload;

    if (exportType === 'excel') {
      yield call(deviceService.exportDevicesToExcel, filters, selectedIds);
    } else if (exportType === 'pdf') {
      yield call(deviceService.exportDevicesToPdf, filters, selectedIds);
    }

    yield put(exportDevicesSuccess());
  } catch (error) {
    const errorMessage = error.message;
    yield put(exportDevicesFailure(errorMessage));
  }
}

function* handleDownloadTemplate() {
  try {
    yield call(deviceService.downloadTemplate);
    yield put(downloadTemplateSuccess());
  } catch (error) {
    const errorMessage = error.message;
    yield put(downloadTemplateFailure(errorMessage));
  }
}

function* handleBulkReapproveDevices(action) {
  try {
    const { ids, statusNote } = action.payload;
    const response = yield call(deviceService.bulkReapproveDevices, ids, statusNote);
    yield put(bulkReapproveDevicesSuccess(response));
    // Refresh devices list after re-approve
    yield put(fetchDevices({
      page: 1,
      pageSize: 10,
      sortColumn: 'deviceCode',
      sortDirection: 'asc'
    }));
  } catch (error) {
    const errorMessage = ERROR_MESSAGES[error.message] || error.message;
    yield put(bulkReapproveDevicesFailure(errorMessage));
  }
}

export function* watchDevicesActions() {
  yield takeLatest(fetchInit.type, handleFetchInit);
  yield takeLatest(fetchDevices.type, handleFetchDevices);
  yield takeLatest(fetchDeviceById.type, handleFetchDeviceById);
  yield takeLatest(addDevice.type, handleAddDevice);
  yield takeLatest(updateDevice.type, handleUpdateDevice);
  yield takeLatest(deleteDevice.type, handleDeleteDevice);
  yield takeLatest(bulkDeleteDevices.type, handleBulkDeleteDevices);
  yield takeLatest(approveDevice.type, handleApproveDevice);
  yield takeLatest(rejectDevice.type, handleRejectDevice);
  yield takeLatest(revokeDevice.type, handleRevokeDevice);
  yield takeLatest(toggleLGSPAccess.type, handleToggleLGSPAccess);
  yield takeLatest(updateConnectionStatus.type, handleUpdateConnectionStatus);
  yield takeLatest(verifyCitizen.type, handleVerifyCitizen);
  yield takeLatest(fetchUnits.type, handleFetchUnits);
  yield takeLatest(assignUnit.type, handleAssignUnit);
  yield takeLatest(importDevices.type, handleImportDevices);
  yield takeLatest(exportDevices.type, handleExportDevices);
  yield takeLatest(downloadTemplate.type, handleDownloadTemplate);
  yield takeLatest(bulkReapproveDevices.type, handleBulkReapproveDevices);
}
// Updated at Tue, Nov 11, 2025 11:09:41 PM
